import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridNoRecords,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import { Loader } from "@progress/kendo-react-indicators";
import { Rating, RatingChangeEvent } from "@progress/kendo-react-inputs";
import { useEffect, useState } from "react";
import {
  AccessPermissionEnum,
  EntityLocalizationGroupEnum,
} from "../../enums/accessPermissionEnum";
import useAuth from "../../hooks/useAuth";
import {
  ScorecardEvaluationDetails,
  ScorecardEvaluationQuestion,
  ShortScorcardType,
} from "../../pages/call-details/RecordingScorecardTab";
import recordingsService from "../../services/recordings.service";
import { RecordingScorecard } from "../../types/recording/RecordingScorecard";
import useSwal from "../../hooks/useSwal";
import { SweetAlertOptions } from "sweetalert2";
import LoadingOverlay from "../LoadingOverlay";
import useMasterData from "../../hooks/useMasterData";
import aiIcon from "../../assets/images/icon-ai.png";
import { concatUsername, getRandomProfileColor } from "../../utils/profileUtils";

interface CustomGridCellProps extends GridCellProps {
  dataItem: ScorecardEvaluationQuestion;
}

interface RecordingScorecardGridProps {
  recordingId: number;
  scorecard: ShortScorcardType;
  questions?: ScorecardEvaluationQuestion[];
  onEvaluationSave: (e: RecordingScorecard) => void;
  error?: string;
  shared?: boolean;
  fetchKeyTranslation: (key: string, defaultVal: string) => string;
  edited: boolean;
  setEdited: (e: boolean) => void;
  sendFetchRecordingScorecardRequest: () => void;
  isEvaluated: boolean;
  setEvaluating: (e: boolean) => void;
  initialScorecard:ShortScorcardType;
  evaluationDetails?: ScorecardEvaluationDetails;
}

const RecordingScorecardGrid: React.FC<RecordingScorecardGridProps> = ({
  recordingId,
  scorecard,
  questions,
  onEvaluationSave,
  error,
  shared,
  fetchKeyTranslation,
  edited,
  setEdited,
  sendFetchRecordingScorecardRequest,
  isEvaluated,
  setEvaluating,
  initialScorecard,
  evaluationDetails
}) => {
  const master = useMasterData();
  const auth = useAuth();
  const swal = useSwal();
  const initialDataState = {
    skip: 0,
    take: 5,
  };
  const [page, setPage] = useState(initialDataState);
  const pageChange = (event: GridPageChangeEvent) => {
    setPage(event.page);
  };

  const [localQuestions, setLocalQuestions] = useState<
    ScorecardEvaluationQuestion[] | undefined
  >();
  const [pending, setPending] = useState<boolean>(false);

  useEffect(() => {
    if (questions) {
      setLocalQuestions(questions);
    }
    setPage(initialDataState);
  }, [questions]);

  const onRatingChange = (questionId: number, rating: number) => {
    const updatedLocalQuestions: ScorecardEvaluationQuestion[] = [];
    localQuestions?.forEach((q) => {
      if (q.id === questionId) {
        updatedLocalQuestions.push({
          ...q,
          questionScore: rating === null ? 0 : rating,
          questionEvaluatedBy: auth?.user ?? null
        });
      } else {
        updatedLocalQuestions.push(q);
      }
    });
    setLocalQuestions(updatedLocalQuestions);
    setEdited(true);
  };

  const onSaveHandler = async () => {
    if (localQuestions) {
      try {
        setPending(true);
        if(initialScorecard.id !==scorecard.id){
          const data = await recordingsService.addRecordingScorecard(
            recordingId,
            scorecard,
            localQuestions,
            shared === true,
            "insert",
            evaluationDetails?.autoQAScore!=null ? evaluationDetails.autoQAScore : null
          );
          onEvaluationSave(data);
        } else {
          const data = await recordingsService.addRecordingScorecard(
            recordingId,
            scorecard,
            localQuestions,
            shared === true,
            "update",
            evaluationDetails?.autoQAScore!=null ? evaluationDetails.autoQAScore : null
          );
          onEvaluationSave(data);
        }
        const swalOptions: SweetAlertOptions = {
          icon: "success",
          title: fetchKeyTranslation(
            "SwtSCCreatedUpdatedTitle",
            "Scorecard Updated"
          ),
          confirmButtonText: fetchKeyTranslation("OKText", "OK"),
        };
        swal.fire(swalOptions);
        setEdited(false);
      } catch (err) {
        const swalOptions: SweetAlertOptions = {
          icon: "error",
          title: fetchKeyTranslation("SwtAltFailedTitle", "Oops..."),
          text: fetchKeyTranslation(
            "SwtAltFailedText",
            "Something went wrong! Please Try Again!"
          ),
        };
        swal.fire(swalOptions);
      } finally {
        setPending(false);
      }
    }
  };

  const onCancelHandler = () => {
    setLocalQuestions(questions);
    setEdited(false);
    if (isEvaluated) {
      sendFetchRecordingScorecardRequest();
    } else {
      setEvaluating(false);
    }
  };

  const customCellMobile = (props: any) => {
    return (
      <td className="listMobile">
        <div className="listRowMobile p-r-40">
          <div className="tblUsr float-left w-100">
            <div className="topUsrAreaPic">
              <div className="topUsrAreaPic-i p-r-12">
                <div className="contact-list-icon bg-darksteelblue">
                  <div className="contact-list-icon-txt">
                    <i className="bi bi-arrow-right-short fs-26"></i>
                  </div>
                </div>
              </div>
              <div className="topUsrAreaPic-tx">
                <span className="text-black fs-14 font-weight-bold cursor-default text-decoration-none float-left w-100 line-height-1 p-b-5">
                  Question
                </span>
                <span className="fs-14 cursor-default text-decoration-none float-left w-100 line-height-1">
                  Hello customer could you provide the your detail for the next
                  level requirement?
                </span>
              </div>
            </div>
          </div>
          <div className="float-left w-100 p-l-50">
            <div className="inerLabel p-t-15 float-left w-100">
              <span className="text-black fs-14 font-weight-bold cursor-default text-decoration-none float-left line-height-1 p-b-5 p-r-5">
                Category:
              </span>
              <span className="fs-14 float-left line-height-1">Financial</span>
            </div>
            <div className="inerLabel p-t-3 float-left w-100 d-flex align-items-center">
              <span className="text-black fs-14 font-weight-bold float-left line-height-1 p-t-5">
                Evaluation:
              </span>
              <span className="float-left line-height-1">
                <span className="ratingStar">
                  <Rating precision={"half"} />
                </span>
              </span>
            </div>
          </div>
        </div>
      </td>
    );
  };

  const CellScoreCardCategory = (props: CustomGridCellProps) => {
    return (
      <td>
        <div>
          {fetchKeyTranslation(
            master?.getEntityNameKeyfromConfigMap(
              EntityLocalizationGroupEnum.ScorecardQuestionGroup,
              props.dataItem.category ?? ""
            ) ?? "",
            props.dataItem.category
          )}
        </div>
      </td>
    );
  };

  const CellScoreCardQuestion = (props: CustomGridCellProps) => {
    return (
      <td className="mx-th-tag">
        <div className="d-flex align-items-center">
          <div className="tblUsr">
            <div className="topUsrAreaPic">
              <div className="topUsrAreaPic-i p-r-12">
                <div className="contact-list-icon bg-darksteelblue">
                  <div className="contact-list-icon-txt">
                    <i className="bi bi-arrow-right-short fs-26"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mx-td-spn line-height-2">
            {fetchKeyTranslation(
              master?.getEntityNameKeyfromConfigMap(
                EntityLocalizationGroupEnum.ScorecardQuestion,
                props.dataItem.question ?? ""
              ) ?? "",
              props.dataItem.question
            )}
          </div>
        </div>
      </td>
    );
  };

  const CustomCellForEvaluation = (props: CustomGridCellProps) => {
  const ques = props.dataItem;
  let questionEvaluatedBy = ques.questionEvaluatedBy ? ques.questionEvaluatedBy : evaluationDetails?.evaluatedBy;
  let userIcon,color;
  if(questionEvaluatedBy){
    color = getRandomProfileColor(questionEvaluatedBy.id);
    const name = concatUsername(
      questionEvaluatedBy.firstName ? questionEvaluatedBy.firstName : "",
      questionEvaluatedBy.lastName ? questionEvaluatedBy.lastName : ""
    ).trim();
    userIcon = questionEvaluatedBy.image ? (
      <div className="contact-list-icon-img">
        <img src="../../assets/images/user-img-2.jpg" />
      </div>
    ) : name !== "" ? (
      <span>
        {name
          .split(" ")
          .map((n: string) => n[0])
          .join("")}
      </span>
    ) : (
      <span className="fs-16">
        <i className="bi bi-person"></i>
      </span>
    );
  }

  const isDisabled =
    scorecard.isArchived ||
    !auth?.checkUserAccess(AccessPermissionEnum.EvaluateCalls) ||
    shared === true;

  return (
    <td>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        
        {/* Rating stars */}
        <div className="ratingStar d-flex align-items-center">
          <Rating
            min={0.5}
            max={5}
            precision="half"
            value={ques.questionScore!=null ? ques.questionScore ?? 0 : ques.autoQAQuestionScore ?? 0}
            onChange={(e: RatingChangeEvent) =>
              onRatingChange(props.dataItem.id, e.value)
            }
            disabled={isDisabled}
          />
        </div>

        <div className="d-flex align-items-center border-left ml-2">

          {/* User icon */}
          {ques.questionScore!=null &&
            <div className="tblUsr ml-2 border-right">
              <div className="topUsrAreaPic">
                <div className="topUsrAreaPic-i">
                  <div className={`contact-list-icon ${color}`} title={`Evaluated by: ${ques.questionEvaluatedBy ? ques.questionEvaluatedBy.email : evaluationDetails?.evaluatedBy?.email}`}>
                    <div className="contact-list-icon-txt">
                      {userIcon}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
          {/* AI rating text with star */}
          {ques.autoQAQuestionScore!=null && ques.questionScore!=null && (
            <div className="text-muted small d-flex align-items-center ml-2">
              <div className="tblUsr">
                <div className="topUsrAreaPic">
                  <div className="topUsrAreaPic-i">
                    <div className="contact-list-icon bg-primary"title="Evaluated by: Auto QA">
                      <div className="contact-list-icon-txt"><img src={aiIcon} className="img-fluid" alt="AI Icon" />
                      </div>
                    </div>
                  </div>
                </div>
              </div> <span className="fs-14">{ques.autoQAQuestionScore}</span>
                <i className="bi bi-star-fill text-warning ml-1"></i>
              </div>
          )}
        </div>
      </div>
    </td>
  );
};

  return (
    <>
      {pending && (
        <LoadingOverlay
          customStyle={{ position: "fixed", marginTop: "55px" }}
          themeColor={"light"}
          size={"medium"}
        />
      )}
      <div className="row">
        <div className="col-md-12">
          <div className="table-container table-responsive table-overflow-hedden borderLeftSecondChild table-mobile">
            <Grid
              data={localQuestions?.slice(page.skip, page.take + page.skip)}
              skip={page.skip}
              take={page.take}
              total={localQuestions?.length}
              pageable={true}
              onPageChange={pageChange}
            >
              <GridNoRecords>
                {!error && !localQuestions && (
                  <Loader themeColor={"primary"} type={"infinite-spinner"} />
                )}
                {error && (
                  <span>
                    <i className="bi bi-exclamation-triangle-fill tx-amber"></i>{" "}
                    {fetchKeyTranslation(
                      "SwtAltFailedText",
                      "Something went wrong! Please Try Again!"
                    )}
                  </span>
                )}
                {localQuestions && (
                  <span>
                    {fetchKeyTranslation(
                      "MsgSCNoQuesFound",
                      "No Scorecard Questions Added."
                    )}
                  </span>
                )}
              </GridNoRecords>
              <GridColumn
                width={0}
                field="cellMobile"
                cell={customCellMobile}
              />
              <GridColumn
                field="question"
                title={fetchKeyTranslation("TitleQuestion", "Question")}
                cell={CellScoreCardQuestion}
              />
              <GridColumn
                field="category"
                title={fetchKeyTranslation("TitleCategory", "Category")}
                cell={CellScoreCardCategory}
                width={200}
              />
              <GridColumn
                field="questionScore"
                title={fetchKeyTranslation("TitleEvaluation", "Evaluation Score")}
                cell={CustomCellForEvaluation}
              />
            </Grid>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="btnGroup p-t-10 text-right">
            {edited && questions && questions.length > 0 && (
              <Button
                className="selectScorecardBtn line-height-1 text-white"
                themeColor={"secondary"}
                style={{ height: "30px" }}
                onClick={onCancelHandler}
              >
                {fetchKeyTranslation("TextCancel", "Cancel")}
              </Button>
            )}
            {edited && questions && questions.length > 0 && (
              <Button
                className="selectScorecardBtn line-height-1 text-white m-l-5"
                themeColor={"primary"}
                style={{ height: "30px" }}
                onClick={onSaveHandler}
              >
                {pending ? (
                  <Loader
                    size={"small"}
                    type={"infinite-spinner"}
                    themeColor={"light"}
                  />
                ) : (
                  fetchKeyTranslation("TextSave", "Save")
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RecordingScorecardGrid;
